import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { cloneDeep } from 'lodash'

import {
  TextField,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  Fab,
  Tooltip,
} from '@mui/material'

import { Add } from '@mui/icons-material'
import { addKeyToObjectsArray } from 'utils/addUniqueKeyToObjects'
import { formatInBrazilianDate, hasAnswerChanged } from 'utils/functions'
import { OPERATION_TYPES } from 'utils/constants/operationTypes'

import styles from './styles'
import AnswerCard from '../AnswerCard'
import QuestionButton from '../QuestionButton'

export default function QuestionSeven(props) {
  const { questionData, saveResponses } = props
  const classes = styles()

  const initialData = {
    role: '',
    developed_activities: '',
    currently_at_this_job: false,
    start_working_hours: null,
    end_working_hours: null,
    reports_to: '',
    end_of_contract: null,
    investment_advisory_start_time: null,
    investment_advisory_end_time: null,
  }

  const initialErrors = {
    role: false,
    developed_activities: false,
    currently_at_this_job: false,
    reports_to: false,
    end_of_contract: false,
    investment_advisory_start_time: false,
    investment_advisory_end_time: false,
  }

  const initializeFormState = (data) => {
    return {
      hasAnotherOccupations: data.items.length > 0,
      items: data.items,
    }
  }

  const [formState, setFormState] = useState(initializeFormState(questionData))

  const [activityData, setActivityData] = useState(initialData)

  const [activityErrors, setActivityErrors] = useState(initialErrors)

  const [loading, setLoading] = useState(false)

  const [edited, setEdited] = useState(false)
  const [saved, setSaved] = useState(questionData.has_answer)

  const handleFieldChange = (event) => {
    const key = event.target.id
    const { value } = event.target
    const newActivityData = cloneDeep(activityData)
    newActivityData[key] = value
    setActivityData(newActivityData)
  }

  const handleRadioChange = (event) => {
    const newFormState = cloneDeep(formState)
    newFormState.hasAnotherOccupations = event.target.value === 'yes'
    setFormState(newFormState)
  }

  const handleStillOnJobRadioChange = (event) => {
    const newActivityData = cloneDeep(activityData)
    newActivityData.currently_at_this_job = event.target.value === 'yes'
    setActivityData(newActivityData)
  }

  const hasAnotherOccupations = () => {
    return !(formState.items.length === 0)
  }

  const validateAndSave = async (skipValidation = false) => {
    if (
      formState.items.length > 0 &&
      formState.hasAnotherOccupations === false
    ) {
      toast.warn(
        'Você deve excluir as atividades antes de atualizar as respostas.',
      )
      return
    }
    const newQuestionData = cloneDeep(questionData)
    if (
      !skipValidation &&
      formState.hasAnotherOccupations &&
      !hasAnotherOccupations()
    ) {
      toast.error(
        'Ao selecionar "Sim" deve-se adicionar ao menos uma atividade',
      )
    } else {
      newQuestionData.has_answer = true
      newQuestionData.items = formState.items
      setLoading(true)
      await saveResponses(
        newQuestionData,
        'representation_activities',
        !saved ? OPERATION_TYPES.CREATE : OPERATION_TYPES.UPDATE,
      )
        .then(() => {
          setSaved(true)
          setEdited(false)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }

  const validateRequiredFields = () => {
    const newActivityErrors = initialErrors

    Object.keys(initialErrors).forEach((key) => {
      if (activityData[key] === '' || activityData[key] === null) {
        if (
          activityData.currently_at_this_job === false &&
          (key === 'start_working_hours' || key === 'end_working_hours')
        ) {
          newActivityErrors[key] = false
          return
        }
        if (
          key === 'bond_informed_as_other' &&
          activityData.bond_type !== 'other'
        ) {
          newActivityErrors[key] = false
          return
        }
        newActivityErrors[key] = true
      }
      if (
        key === 'end_of_contract' &&
        (activityData[key] === '' || activityData[key] === null) &&
        activityData.currently_at_this_job
      ) {
        newActivityErrors[key] = false
        return
      }
      if (key === 'bond_type' && activityData[key] === 'default') {
        newActivityErrors[key] = true
      }
    })

    setActivityErrors(newActivityErrors)

    return !Object.keys(newActivityErrors).some((key) => newActivityErrors[key])
  }

  const handleValidateAndUpdateState = () => {
    if (validateRequiredFields()) {
      const newFormState = cloneDeep(formState)
      newFormState.items.push(activityData)
      setFormState(newFormState)
      setActivityData(initialData)
    } else {
      toast.warn(
        'Favor, verifique se todos os campos obrigatórios foram preenchidos corretamente',
      )
    }
  }

  const handleRemoveOccupation = (position) => {
    const newFormState = cloneDeep(formState)
    newFormState.items = newFormState.items.filter(
      (activity, index) => index !== position,
    )
    setFormState(newFormState)
  }

  const buildOccupation = (occupationInfo) => {
    return (
      <React.Fragment>
        <p>
          <span>Cargo: </span>
          {occupationInfo.role}
        </p>

        <p>
          <span>Atividades desempenhadas: </span>
          {occupationInfo.developed_activities}
        </p>
        <p>
          <span>Reporta/reportava-se a: </span>
          {occupationInfo.reports_to}
        </p>
        {occupationInfo.end_of_contract ? (
          <p>
            <span>Fim do vínculo: </span>
            {formatInBrazilianDate(occupationInfo.end_of_contract)}
          </p>
        ) : null}

        {occupationInfo.currently_at_this_job ? (
          <p>
            <span>Jornada de trabalho: </span>
            {` ${occupationInfo.start_working_hours} às ${occupationInfo.end_working_hours}.`}
          </p>
        ) : null}
        <p>
          <span>Jornada da atividade de assessoria: </span>
          {` ${occupationInfo.investment_advisory_start_time} às ${occupationInfo.investment_advisory_end_time}.`}
        </p>
      </React.Fragment>
    )
  }

  useEffect(() => {
    const hasChanged =
      hasAnswerChanged(questionData.items, formState.items) ||
      formState.hasAnotherOccupations !== questionData.items.length > 0
    setEdited(hasChanged)
    if (
      formState.items.length > 0 &&
      formState.hasAnotherOccupations === false
    ) {
      toast.warn(
        'Você deve excluir as atividades antes de atualizar as respostas.',
      )
    }
  }, [formState])

  useEffect(() => {
    setSaved(questionData.has_answer)
    setFormState(initializeFormState(questionData))
  }, [questionData])

  useEffect(() => {
    if (hasAnswerChanged(questionData.items, formState.items))
      validateAndSave(true)
  }, [formState.items])

  return (
    <div className={classes.root}>
      <div className={classes.formContainer}>
        <h3>Questão 7</h3>
        <div className={classes.form}>
          <FormControl>
            <FormLabel>
              Exerce alguma atividade de representação ou governança em cargos
              de Conselhos, Diretorias, Comissões, Associações, Bolsas, etc. em
              entidades financeiras?{' '}
            </FormLabel>
            <RadioGroup
              row
              value={formState.hasAnotherOccupations ? 'yes' : 'no'}
              name="hasRelations"
              onChange={handleRadioChange}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Sim" />
              <FormControlLabel value="no" control={<Radio />} label="Não" />
            </RadioGroup>
          </FormControl>

          {formState.hasAnotherOccupations ? (
            <div className={classes.formFields}>
              <TextField
                id="role"
                label="Cargo"
                onChange={handleFieldChange}
                required
                fullWidth
                value={activityData.role}
                error={activityErrors.role}
                size="small"
              />

              <TextField
                id="developed_activities"
                label="Atividades desempenhadas"
                onChange={handleFieldChange}
                required
                fullWidth
                value={activityData.developed_activities}
                error={activityErrors.developed_activities}
                size="small"
              />

              <TextField
                id="reports_to"
                label="A quem se reporta/reportava na estrutura organizacional do órgão?"
                onChange={handleFieldChange}
                error={activityErrors.reports_to}
                value={activityData.reports_to}
                required
                fullWidth
                size="small"
              />
              <FormControl className={classes.radioQuestion}>
                <FormLabel id="">
                  Após o credenciamento continuará exercendo essa função?
                </FormLabel>
                <RadioGroup
                  row
                  value={activityData.currently_at_this_job ? 'yes' : 'no'}
                  name="currently_at_this_job"
                  onChange={handleStillOnJobRadioChange}
                >
                  <FormControlLabel
                    value="yes"
                    control={<Radio />}
                    label="Sim"
                  />
                  <FormControlLabel
                    value="no"
                    control={<Radio />}
                    label="Não"
                  />
                </RadioGroup>
              </FormControl>
              {activityData.currently_at_this_job ? (
                <div className={classes.times}>
                  <FormLabel>Jornada de trabalho:</FormLabel>
                  <div>
                    <TextField
                      id="start_working_hours"
                      label="Início"
                      type="time"
                      onChange={handleFieldChange}
                      error={activityErrors.start_working_hours}
                      value={activityData.start_working_hours || ''}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      size="small"
                    />

                    <TextField
                      id="end_working_hours"
                      label="Fim"
                      type="time"
                      onChange={handleFieldChange}
                      error={activityErrors.end_working_hours}
                      value={activityData.end_working_hours || ''}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      size="small"
                    />
                  </div>
                </div>
              ) : (
                <TextField
                  id="end_of_contract"
                  label="Fim do vínculo"
                  onChange={handleFieldChange}
                  error={activityErrors.end_of_contract}
                  value={activityData.end_of_contract || ''}
                  fullWidth
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              )}

              <div className={classes.times}>
                <FormLabel>Jornada da atividade de assessoria:</FormLabel>
                <div>
                  <TextField
                    id="investment_advisory_start_time"
                    label="Início"
                    type="time"
                    onChange={handleFieldChange}
                    error={activityErrors.investment_advisory_start_time}
                    value={activityData.investment_advisory_start_time || ''}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    size="small"
                  />

                  <TextField
                    id="investment_advisory_end_time"
                    label="Fim"
                    type="time"
                    onChange={handleFieldChange}
                    error={activityErrors.investment_advisory_end_time}
                    value={activityData.investment_advisory_end_time || ''}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    size="small"
                  />
                </div>
              </div>

              <div className={classes.fabContainer}>
                <Tooltip title="Adicionar atividade">
                  <Fab
                    onClick={handleValidateAndUpdateState}
                    disabled={formState?.developed_activities?.length === 10}
                    size="small"
                    color="primary"
                  >
                    <Add />
                  </Fab>
                </Tooltip>
              </div>
            </div>
          ) : null}
        </div>
        {formState?.items?.length > 0 ? (
          <div className={classes.answersContainer}>
            {addKeyToObjectsArray(formState.items)?.map((activity, index) => (
              <AnswerCard
                key={activity.key}
                index={index}
                handleRemove={handleRemoveOccupation}
                mustRemove={formState.hasAnotherOccupations === false}
              >
                {buildOccupation(activity)}
              </AnswerCard>
            ))}
          </div>
        ) : null}
      </div>
      <div className={classes.buttonsContainer}>
        <QuestionButton
          variant="contained"
          onClick={validateAndSave}
          loading={loading}
          saved={saved}
          edited={edited}
          disabled={
            (formState.hasAnotherOccupations && formState.items.length === 0) ||
            (saved && !edited)
          }
        />
      </div>
    </div>
  )
}
